import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, Lightbulb, Zap, Target, Settings } from 'lucide-react';

const OpticalTransistorOperation = () => {
  const steps = [
    {
      id: 1,
      title: "Coherent Input Light Beams",
      description: "Control and data beams enter the optical transistor device",
      icon: Lightbulb,
      details: "Two coherent light beams — a control beam and a data beam — are precisely directed into the optical transistor. These beams carry information through their wave properties.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: 2,
      title: "Light Wave Interference",
      description: "At the intersection zone, interference of light waves occurs",
      icon: Zap,
      details: "When the beams intersect, they create interference patterns that modulate the intensity and phase of the output signal through direct photon interaction.",
      color: "from-purple-500 to-indigo-500"
    },
    {
      id: 3,
      title: "Logical Output Formation",
      description: "The resulting light beam forms a logical output value (0 or 1)",
      icon: Target,
      details: "Constructive and destructive interference create distinct logical states. The wave patterns determine whether the output represents binary 0 or 1.",
      color: "from-green-500 to-emerald-500"
    },
    {
      id: 4,
      title: "Complex Logic Circuit Building",
      description: "Output signals can be used to build complex logic circuits and processors",
      icon: Settings,
      details: "These optical logic operations can be cascaded and combined to create sophisticated processors, maintaining TTL compatibility for seamless integration.",
      color: "from-orange-500 to-red-500"
    }
  ];

  return (
    <div className="w-full max-w-6xl mx-auto">
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
        <CardHeader className="text-center pb-6">
          <CardTitle className="text-2xl md:text-3xl text-slate-900 mb-2">
            OpticalDT Optical Transistor Operation
          </CardTitle>
          <CardDescription className="text-lg text-slate-600">
            Step-by-Step Working Principle
          </CardDescription>
        </CardHeader>
        
        <CardContent className="p-6 md:p-8">
          {/* Step-by-Step Visual Timeline */}
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-16 left-20 right-20 h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 via-green-200 to-orange-200"></div>
            
            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {steps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={step.id} className="relative">
                    {/* Step Card */}
                    <Card className="h-full border-2 border-slate-200 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
                      <CardHeader className="text-center pb-4">
                        {/* Step Number Badge */}
                        <div className="flex justify-center mb-4">
                          <Badge className="bg-slate-600 text-white text-lg px-4 py-2 rounded-full">
                            {step.id}
                          </Badge>
                        </div>
                        
                        {/* Icon */}
                        <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                        
                        {/* Title */}
                        <CardTitle className="text-lg text-slate-900 mb-2 leading-tight">
                          {step.title}
                        </CardTitle>
                        
                        {/* Description */}
                        <CardDescription className="text-sm text-slate-600 font-medium">
                          {step.description}
                        </CardDescription>
                      </CardHeader>
                      
                      <CardContent>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {step.details}
                        </p>
                      </CardContent>
                    </Card>
                    
                    {/* Arrow between steps (desktop only) */}
                    {index < steps.length - 1 && (
                      <div className="hidden lg:block absolute top-16 -right-4 z-10">
                        <div className="w-8 h-8 bg-white border-2 border-slate-300 rounded-full flex items-center justify-center shadow-md">
                          <ArrowRight className="w-4 h-4 text-slate-600" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Key Principles Summary */}
          <div className="mt-12 p-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl">
            <h4 className="text-xl font-bold mb-4 text-center">Key Operating Principles</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Lightbulb className="w-6 h-6" />
                </div>
                <h5 className="font-semibold mb-2">Direct Light Control</h5>
                <p className="opacity-90">Light controls light directly without electrical conversion</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6" />
                </div>
                <h5 className="font-semibold mb-2">Speed of Light Processing</h5>
                <p className="opacity-90">Operations occur at light speed with minimal energy loss</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Settings className="w-6 h-6" />
                </div>
                <h5 className="font-semibold mb-2">TTL Compatibility</h5>
                <p className="opacity-90">Seamless integration with existing electronic systems</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OpticalTransistorOperation;